import type { ComponentType, ReactNode } from 'react'
import { ArrowRight, CaretRight, PhoneCall, Plus, type IconProps } from '@phosphor-icons/react'
import '@fontsource-variable/onest'
import { DialogPanel } from '@/landing/Dialog'
import { DEMO_PHONE, DEMO_PHONE_LABEL, PILOT_CTA, SIGNUP_CTA, type DialogLine, type DialogResult } from '@/landing/content'
import { BLOG_POSTS } from './blog'
import { FEATURE_PAGES } from './features'
import { INDUSTRY_PAGES } from './industries'
import { breadcrumbList, type Crumb, type PageMeta } from './meta'
import { findSitePage } from './pages'
import { SiteShell } from './SiteShell'

/* ── Text ────────────────────────────────────────────────────────────── */

/** `[текст](/шлях)` is a link; `[TODO: …]` is a placeholder the owner has to
 * fill. A page that still shows one is kept out of search by the prerender
 * (scripts/prerender.mjs), so a draft can't get indexed by accident. */
const TOKEN = /\[TODO:[^\]]*\]|\[([^\]]+)\]\(([^)\s]+)\)/g

export function Rich({ text }: { text: string }) {
  const out: ReactNode[] = []
  let last = 0
  for (const m of text.matchAll(TOKEN)) {
    const at = m.index ?? 0
    if (at > last) out.push(text.slice(last, at))
    if (m[1] !== undefined) out.push(<a key={at} href={m[2]}>{m[1]}</a>)
    else out.push(<mark key={at} className="lp-todo">{m[0]}</mark>)
    last = at + m[0].length
  }
  if (last < text.length) out.push(text.slice(last))
  return <>{out}</>
}

/** The same text without link markup, for JSON-LD and meta tags. */
export function plain(text: string): string {
  return text.replace(/\[([^\]]+)\]\(([^)\s]+)\)/g, '$1')
}

export type Block =
  | string
  | { h3: string }
  | { list: string[] }
  | { steps: { title: string; text: string }[] }
  | { note: string }

export interface Section {
  h2: string
  blocks: Block[]
}

export function Blocks({ blocks }: { blocks: Block[] }) {
  return (
    <>
      {blocks.map((b, i) => {
        if (typeof b === 'string') return <p key={i}><Rich text={b} /></p>
        if ('h3' in b) return <h3 key={i}>{b.h3}</h3>
        if ('list' in b) {
          return <ul key={i}>{b.list.map((x) => <li key={x}><Rich text={x} /></li>)}</ul>
        }
        if ('steps' in b) {
          return (
            <ol key={i} className="lp-prose-steps">
              {b.steps.map((s) => (
                <li key={s.title}>
                  <strong>{s.title}</strong>
                  <span><Rich text={s.text} /></span>
                </li>
              ))}
            </ol>
          )
        }
        return <p key={i} className="lp-prose-note"><Rich text={b.note} /></p>
      })}
    </>
  )
}

export function Sections({ sections }: { sections: Section[] }) {
  return (
    <>
      {sections.map((s) => (
        <section key={s.h2} className="lp-prose-section">
          <h2>{s.h2}</h2>
          <Blocks blocks={s.blocks} />
        </section>
      ))}
    </>
  )
}

/* ── Page parts ──────────────────────────────────────────────────────── */

export function Crumbs({ items }: { items: Crumb[] }) {
  return (
    <nav className="lp-crumbs" aria-label="Ви тут">
      <ol>
        {items.map((c, i) => (
          <li key={c.path}>
            {i < items.length - 1
              ? <><a href={c.path}>{c.name}</a><CaretRight size={12} weight="bold" aria-hidden="true" /></>
              : <span aria-current="page">{c.name}</span>}
          </li>
        ))}
      </ol>
    </nav>
  )
}

export interface QA {
  q: string
  a: string
}

export function FaqList({ items }: { items: QA[] }) {
  return (
    <div className="lp-faq">
      {items.map((f) => (
        <details key={f.q}>
          <summary>
            {f.q}
            <Plus size={18} weight="bold" className="lp-faq-icon" />
          </summary>
          <p><Rich text={f.a} /></p>
        </details>
      ))}
    </div>
  )
}

export function CtaRow() {
  return (
    <div className="lp-page-cta">
      <a href="/register" className="lp-btn lp-btn-primary">
        {SIGNUP_CTA} <ArrowRight size={18} weight="bold" />
      </a>
      <a href="/#pilot" className="lp-btn lp-btn-ghost">{PILOT_CTA}</a>
    </div>
  )
}

export function CtaBand({ title = 'Почуйте, як агент відповідає вашим клієнтам' }: { title?: string }) {
  return (
    <section className="lp-wrap lp-cta-band">
      <div>
        <h2>{title}</h2>
        <p className="lp-muted">
          <PhoneCall size={18} weight="duotone" aria-hidden="true" /> Подзвоніть на демо-лінію{' '}
          <a href={`tel:${DEMO_PHONE}`}>{DEMO_PHONE_LABEL}</a> і поговоріть з агентом як клієнт: це займе 30 секунд.
        </p>
      </div>
      <CtaRow />
    </section>
  )
}

export interface Card {
  path: string
  title: string
  text: string
  icon?: ComponentType<IconProps>
}

const CATALOG: Card[] = [
  ...INDUSTRY_PAGES,
  ...FEATURE_PAGES,
  ...BLOG_POSTS.map((p) => ({ path: p.path, title: p.title, text: p.description })),
  { path: '/tsiny', title: 'Ціни', text: 'З чого складається оплата і що входить у пробний період.' },
  { path: '/faq', title: 'Часті питання', text: 'Підключення, номер, дані клієнтів, вартість.' },
  { path: '/dlya', title: 'Галузі', text: 'Як агент працює в клініках, автосервісах, ресторанах, агенціях і салонах.' },
  { path: '/funktsii', title: 'Усі можливості', text: 'AI-агент, сценарії, записи, CRM, Telegram, робота 24/7.' },
  { path: '/blog', title: 'Блог', text: 'Практичні статті про дзвінки і клієнтів для малого бізнесу.' },
]

/** `level`: h2 when the cards come right after the page's h1 (hubs). */
export function Cards({ items, level: Heading = 'h3' }: { items: Card[]; level?: 'h2' | 'h3' }) {
  return (
    <ul className="lp-ind-cards">
      {items.map(({ path, title, text, icon: Icon }) => (
        <li key={path}>
          <a href={path}>
            {Icon && <Icon size={28} weight="duotone" aria-hidden="true" />}
            <Heading>{title}</Heading>
            <p>{text}</p>
            <span className="lp-more">Детальніше <ArrowRight size={16} weight="bold" /></span>
          </a>
        </li>
      ))}
    </ul>
  )
}

/** "See also": only pages that exist, in the order given. */
export function Related({ paths, title = 'Дивіться також' }: { paths: string[]; title?: string }) {
  const items = paths
    .filter((p) => findSitePage(p))
    .map((p) => CATALOG.find((c) => c.path === p))
    .filter((c): c is Card => Boolean(c))
  if (!items.length) return null
  return (
    <section className="lp-wrap lp-related">
      <h2>{title}</h2>
      <Cards items={items} />
    </section>
  )
}

/* ── Industry, feature and hub pages ─────────────────────────────────── */

export interface ContentPageData {
  /** Shown in the breadcrumbs. */
  crumb: string
  parent?: Crumb
  path: string
  title: string
  description: string
  eyebrow: string
  h1: string
  lead: string
  /** Hub pages: the pages of this section, right under the header. */
  cards?: Card[]
  sections: Section[]
  dialog?: { h2: string; context?: string; dialog: DialogLine[]; result: DialogResult }
  after?: Section[]
  faq?: QA[]
  related?: string[]
}

function trail(page: { crumb: string; path: string; parent?: Crumb }): Crumb[] {
  return [{ name: 'Головна', path: '/' }, ...(page.parent ? [page.parent] : []), { name: page.crumb, path: page.path }]
}

export function contentMeta(page: ContentPageData): PageMeta {
  return { title: page.title, description: page.description, schema: [breadcrumbList(trail(page))] }
}

export function ContentPage({ page }: { page: ContentPageData }) {
  return (
    <SiteShell>
      <main id="main" className="lp-page">
        <div className="lp-wrap">
          <Crumbs items={trail(page)} />
          <header className="lp-page-head">
            <p className="lp-eyebrow">{page.eyebrow}</p>
            <h1>{page.h1}</h1>
            <p className="lp-page-lead"><Rich text={page.lead} /></p>
            <CtaRow />
          </header>
        </div>

        {page.cards && <div className="lp-wrap lp-page-cards"><Cards items={page.cards} level="h2" /></div>}

        <div className="lp-wrap lp-prose"><Sections sections={page.sections} /></div>

        {page.dialog && (
          <section className="lp-wrap lp-page-dialog">
            <h2>{page.dialog.h2}</h2>
            <DialogPanel context={page.dialog.context} dialog={page.dialog.dialog} result={page.dialog.result} />
          </section>
        )}

        {page.after && <div className="lp-wrap lp-prose"><Sections sections={page.after} /></div>}

        {page.faq && (
          <section className="lp-wrap lp-page-faq">
            <h2>Питання і відповіді</h2>
            <FaqList items={page.faq} />
          </section>
        )}

        <CtaBand />
        {page.related && <Related paths={page.related} />}
      </main>
    </SiteShell>
  )
}
